import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './SessionContextMenu.css';

export interface SessionContextMenuProps {
  /** Anchor point in viewport coordinates (pointer position). */
  x: number;
  y: number;
  onDelete: () => void;
  onClose: () => void;
}

/**
 * A small positioned menu (currently just Delete) anchored at the pointer.
 * Opened by SessionsPanel on right-click (desktop) or long-press (touch).
 * Closes on outside pointer-down or Escape; clamps within the viewport so it
 * never overflows the edge.
 */
export default function SessionContextMenu({
  x,
  y,
  onDelete,
  onClose,
}: SessionContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });

  // Clamp within the viewport after the menu has a measured size.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) {
      return;
    }
    const { width, height } = el.getBoundingClientRect();
    const margin = 8;
    setPos({
      x: Math.min(x, window.innerWidth - width - margin),
      y: Math.min(y, window.innerHeight - height - margin),
    });
  }, [x, y]);

  useEffect(() => {
    menuRef.current?.querySelector('button')?.focus();
    const onPointerDown = (e: PointerEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    // Capture phase so an outside press closes before it does anything else.
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="om-ctxmenu"
      role="menu"
      style={{ left: pos.x, top: pos.y }}
    >
      <button
        type="button"
        role="menuitem"
        className="om-ctxmenu__item om-ctxmenu__item--danger"
        onClick={onDelete}
      >
        Delete session
      </button>
    </div>
  );
}
